import {Component, AfterViewInit, OnDestroy, Input, OnChanges, SimpleChanges, ViewChild, ElementRef} from '@angular/core';
import { Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import Prism from 'prismjs';
import 'prismjs/components/prism-ruby.min.js';
import 'prismjs/components/prism-python.min.js';
import 'prismjs/components/prism-bash.min.js';
import 'prismjs/components/prism-java.min.js';
import 'prismjs/components/prism-json.min.js';
import 'prismjs/components/prism-markup.min.js';
import jsPDF from 'jspdf';
import {Campaign, CustomDomain, DeploymentLink} from '../_models';
import {AlertService} from '../_services';

interface PdfLink {
  left: number;
  top: number;
  width: number;
  height: number;
  url: string;
  targetTop: number;
}

@Component({
  selector: 'app-solvpath-documentation',
  templateUrl: './solvpath-documentation.component.html',
  styleUrls: ['./solvpath-documentation.component.css']
})
export class SolvpathDocumentationComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input('brand') brand: Campaign;
  @Input('domain') domain: CustomDomain;
  @Input('link') link: DeploymentLink;
  @ViewChild('documentation', { static: true }) documentationRef: ElementRef<HTMLElement>;

  private highlightInterval: any;
  selectedLanguage: string = 'Python'; // Default to Python
  responseUrl: string = '';
  isPDFLoading = false;
  examplePayload = {
    "customer_id": '"customer_id": 1234'
  }

  payloadFields = `
    "customer_id": "string",      // CRM Customer ID. Example: "1234"
    "email": "string",            // Customer's email address
    "order_number": "string",     // CRM Order ID. Example: "12345678"
    "product_id": "string",       // CRM Product ID of the order's line item. Example: "12"
    "step_type": "integer",       // Path's step type to start the session on
    "step": "string",             // Path's step to start the session on. Examples: "slug", "select-your-item", "faqs"
    "is_first_step": "boolean",   // If true, force the step to be the first step of the path
    "tracking_source": "integer", // Tracking Source ID. Example: 1
    "sid1": "string",             // Tracking Sub ID 1. Example: "sub1"
    "sid2": "string",             // Tracking Sub ID 2. Example: "sub2"
    "sid3": "string"              // Tracking Sub ID 3. Example: "sub3"
    "language": "string"          // Language code for localization. Example: "fr"
    "session_type": "integer"     // Optional portal type to start the session on
  `

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private alertService: AlertService) { }

  ngAfterViewInit() {
    this.highlightInterval = setInterval(() => {
      Prism.highlightAll();
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.brand) {
      this.generateUrl()
    }
  }

  openLanguage(language: string): void {
    this.selectedLanguage = language;
    setTimeout(() => {
      Prism.highlightAll();
    }, 0);
  }

  scrollToElementInModal(elementId: string) {
    const element = this.document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  ngOnDestroy() {
    if (this.highlightInterval) {
      clearInterval(this.highlightInterval); // Clear the interval on component destruction
    }
  }

  get apiPath() {
    if (this.domain && this.domain.id) {
      return 'custom_domains/' + this.domain.id.toString() + '/start_session/';
    }
    if (this.brand) {
      return 'campaigns/' + this.brand.id.toString() + '/start_session/';
    }
  }

  examplePayloadValues(language: string) {
    let payload = ``
    const values = Object.values(this.examplePayload)
    for(let index in values) {
      const isLast = +index === (values.length - 1)
      payload += `  ${values[index]}${isLast ? '' : ','}`
      if (language !== 'Java') {
        payload += `\n`;
      }
    }
    if (language === 'PHP') {
      payload = payload.split(':').join(' =>');
    }
    return payload
  }

  generateUrl() {
    let url = `https://${this.domain ? this.domain.domain : this.brand.active_domain}/start/sEsSIoN-123456789abcd/`;
    let separator = '?';
    let checkFirstStep = false;

    if (this.link) {
      if (this.link.step_type) {
        this.examplePayload["step_type"] = `"step_type": "${this.link.step_type}"`;
        checkFirstStep = true;
      } else if (this.link.step_key && this.link.step_key.length) {
        url += this.link.step_key && this.link.step_key;
        this.examplePayload["step"] = `"step": "${this.link.step_key}"`;
        checkFirstStep = true;
      }

      if (checkFirstStep && this.link.is_first_step) {
        this.examplePayload["is_first_step"] = 'is_first_step: true';
      }

      if (this.link.session_type) {
        this.examplePayload["session_type"] = `"session_type": "${this.link.session_type}"`;
      }

      if (this.link.tracking_source) {
        url += separator + 'tracking_source=' + this.link.tracking_source['id'];
        this.examplePayload["tracking_source"] = `"tracking_source": "${this.link.tracking_source['id']}"`;
        separator = '&';
      }

      ['sid1', 'sid2', 'sid3', 'language'].forEach((key: string) => {
        let value: string = this.link[key];
        if (value && value.length) {
          value = value.trim();
          if (value.length) {
            url += separator + key + '=' + value.toLowerCase();
            this.examplePayload[`"${key}"`] = `"${key}": "${value.toLowerCase()}"`;
          }
        }
      });
    } else {
      url += 'enter_product_funnel';
    }

    this.responseUrl = url;
  }

  downloadPDF() {
    const container = this.documentationRef.nativeElement;
    const pdf = new jsPDF('p', 'mm', 'a4');
    const margin = 10;
    const width = pdf.internal.pageSize.getWidth() - margin * 2;
    const pageContentHeight = pdf.internal.pageSize.getHeight() - margin * 2;
    const windowWidth = container.offsetWidth;
    const mmPerPx = width / windowWidth;
    let links: PdfLink[] = [];

    this.isPDFLoading = true;
    Prism.highlightAllUnder(container);

    pdf.html(container, {
      margin: [margin, margin, margin, margin],
      autoPaging: 'slice',
      width: width,
      windowWidth: windowWidth,
      html2canvas: {
        backgroundColor: '#f5f5f5',
        onclone: (clonedDocument: HTMLDocument) => {
          const root = clonedDocument.querySelector('.html2pdf__container').firstElementChild as HTMLElement;
          root.classList.add('pdf-export');
          this.prepareForPdf(root, pageContentHeight / mmPerPx, pdf.internal.scaleFactor);
          links = this.collectPdfLinks(root);
        }
      },
      callback: (doc: jsPDF) => {
        links.forEach((link) => {
          const top = link.top * mmPerPx;
          const page = Math.floor(top / pageContentHeight);
          let options: object = { url: link.url };
          if (link.targetTop !== null) {
            options = { pageNumber: Math.floor(link.targetTop * mmPerPx / pageContentHeight) + 1 };
          }
          doc.setPage(page + 1);
          doc.link(
            margin + link.left * mmPerPx,
            margin + top - page * pageContentHeight,
            link.width * mmPerPx,
            link.height * mmPerPx,
            options
          );
        });
        doc.save(this.pdfFileName);
        this.isPDFLoading = false;
      }
    }).catch((error) => {
      console.error(error);
      this.alertService.error('Could not generate the PDF. Please try again.');
      this.isPDFLoading = false;
    });
  }

  private prepareForPdf(root: HTMLElement, pageHeightPx: number, scaleFactor: number) {
    const headings = ['H1', 'H2', 'H3', 'H4'];
    const view = root.ownerDocument.defaultView;

    [root].concat(Array.from(root.querySelectorAll<HTMLElement>('*'))).forEach((el) => {
      const computed = view.getComputedStyle(el);
      const fontWeight = parseInt(computed.fontWeight, 10);
      const fontSize = Math.round(parseFloat(computed.fontSize) * scaleFactor) / scaleFactor + 0.01;
      const fontFamily = el.closest('pre, code') ? '"Courier New", Courier, monospace' : 'Helvetica, Arial, sans-serif';
      el.style.setProperty('font-family', fontFamily, 'important');
      el.style.setProperty('font-weight', fontWeight >= 600 || el.tagName === 'H1' ? 'bold' : 'normal', 'important');
      el.style.setProperty('font-size', `${fontSize}px`, 'important');
    });

    Array.from(root.querySelectorAll<HTMLElement>('pre, pre code')).forEach((el) => {
      el.style.whiteSpace = 'pre-wrap';
      el.style.wordBreak = 'break-word';
      el.style.overflow = 'visible';
    });

    const rootTop = root.getBoundingClientRect().top;
    const topOf = (node: Element) => node.getBoundingClientRect().top - rootTop;
    const firstAncestor = (node: HTMLElement) => {
      let current = node;
      while (current.parentElement !== root && !current.previousElementSibling) {
        current = current.parentElement;
      }
      return current;
    };

    Array.from(root.querySelectorAll<HTMLElement>('h1, h2, h3, h4, p, pre')).forEach((el) => {
      const bottom = el.getBoundingClientRect().bottom - rootTop;
      const previous = el.previousElementSibling as HTMLElement;
      let target = firstAncestor(el);
      if (previous && headings.includes(previous.tagName) && bottom - topOf(firstAncestor(previous)) < pageHeightPx) {
        target = firstAncestor(previous);
      }

      const targetTop = topOf(target);
      const pageEnd = (Math.floor(targetTop / pageHeightPx) + 1) * pageHeightPx;

      if (bottom > pageEnd && bottom - targetTop < pageHeightPx) {
        const currentMargin = parseFloat(view.getComputedStyle(target).marginTop) || 0;
        const shift = pageEnd - targetTop + 16;
        target.style.marginTop = `${currentMargin + shift}px`;
        const moved = topOf(target) - targetTop;
        target.style.marginTop = `${currentMargin + shift * 2 - moved}px`;
      }
    });
  }

  private collectPdfLinks(root: HTMLElement): PdfLink[] {
    const rootRect = root.getBoundingClientRect();
    const links: PdfLink[] = [];

    Array.from(root.querySelectorAll<HTMLAnchorElement>('a')).forEach((anchor) => {
      const targetId = anchor.getAttribute('data-pdf-target');
      const href = anchor.getAttribute('href');
      let url = null;
      let targetTop = null;

      if (targetId) {
        const target = root.querySelector(`#${targetId}`);
        if (target) {
          targetTop = target.getBoundingClientRect().top - rootRect.top;
        }
      } else if (href && !href.startsWith('javascript:')) {
        url = new URL(href, this.document.baseURI).href;
      }

      if (url || targetTop !== null) {
        Array.from(anchor.getClientRects()).forEach((rect) => {
          links.push({
            left: rect.left - rootRect.left,
            top: rect.top - rootRect.top,
            width: rect.width,
            height: rect.height,
            url: url,
            targetTop: targetTop
          });
        });
      }
    });

    return links;
  }

  private get pdfFileName() {
    const name = this.domain ? this.domain.domain : (this.brand ? this.brand.name : '');
    const slug = (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    return `solvpath-documentation${slug ? '-' + slug : ''}.pdf`;
  }
}
